import { z } from 'zod';
import {
  ConfirmationStatusSchema,
  type Confirmation,
  type ConfirmationStatus,
  type ScheduleItem,
} from '../../domain/entities/schedule.ts';
import { expectUnchanged, updateWrite, type BatchWrite } from '../../ports/batch.ts';
import { NotFoundError } from '../../ports/repository.ts';
import { listScheduleItems } from './save-schedule-item.ts';
import type { SchedulePlanContext } from './schedule-plan.ts';

export interface ConfirmationCounts {
  readonly accepted: number;
  readonly pending: number;
  readonly declined: number;
  /** Assigned subs who have not been asked yet. */
  readonly unasked: number;
}

/**
 * How an item's subs have answered.
 *
 * `unasked` is reported separately from `pending` on purpose. "Nobody has
 * replied" and "nobody has been asked" look identical in a count of zero
 * accepted, and they call for opposite actions.
 */
export function confirmationCounts(item: ScheduleItem): ConfirmationCounts {
  const subs = item.assignees.filter((assignee) => assignee.kind === 'contact');
  let accepted = 0;
  let pending = 0;
  let declined = 0;

  for (const sub of subs) {
    const status = item.confirmations[sub.id]?.status;
    if (status === 'accepted') accepted += 1;
    else if (status === 'declined') declined += 1;
    else if (status === 'pending') pending += 1;
  }

  return { accepted, pending, declined, unasked: subs.length - accepted - pending - declined };
}

/**
 * Ask every assigned sub who has not been asked to confirm their dates.
 *
 * Existing answers are left alone: re-requesting would wipe an accept
 * somebody has already given, and the whole point of a confirmation is that
 * it is a record of what they said.
 *
 * Refuses on an offline schedule. Nothing should ask a sub to confirm dates
 * they cannot see, and the rules deny them the read, so the request would be
 * unanswerable.
 */
export async function requestConfirmations(
  context: SchedulePlanContext,
  itemId: string,
  options: { readonly scheduleOnline: boolean },
): Promise<{ requested: number }> {
  if (!options.scheduleOnline) {
    throw new ConfirmationError(
      'Take the schedule online first — a sub cannot see dates they have not been shown.',
    );
  }

  const repo = context.data.collection<ScheduleItem>(
    { companyId: context.companyId, jobId: context.jobId },
    'scheduleItems',
  );
  const item = await repo.get(itemId);
  if (!item) throw new NotFoundError('scheduleItems', itemId);

  const now = context.clock.now().toISOString();
  const confirmations: Record<string, Confirmation> = { ...item.confirmations };
  let requested = 0;

  for (const assignee of item.assignees) {
    if (assignee.kind !== 'contact' || confirmations[assignee.id]) continue;
    confirmations[assignee.id] = { status: 'pending', at: now };
    requested += 1;
  }

  if (requested === 0) return { requested: 0 };

  await context.data.commit([
    updateWrite<ScheduleItem>(
      { companyId: context.companyId, jobId: context.jobId },
      'scheduleItems',
      itemId,
      { confirmations, updatedAt: now, updatedBy: context.uid },
      expectUnchanged(item),
    ),
  ]);

  return { requested };
}

export class ConfirmationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfirmationError';
  }
}

export const RespondInputSchema = z.object({
  status: ConfirmationStatusSchema.refine(
    (status): status is 'accepted' | 'declined' => status !== 'pending',
    { message: 'Answer with accepted or declined' },
  ),
  note: z.string().trim().max(1000).optional(),
});
export type RespondInput = z.input<typeof RespondInputSchema>;

/**
 * Record a sub's answer to the dates on one item.
 *
 * This is the only write anyone outside the company makes to a schedule, so
 * it is deliberately narrow: one key of one map on one item. Security rules
 * enforce the same shape independently — a sub may change their own
 * confirmation and nothing else, and only while the schedule is online.
 *
 * `contactId` comes from the caller's own membership, never from the
 * request. A sub answering for another sub is the obvious thing to try.
 */
export async function respondToConfirmation(
  context: SchedulePlanContext,
  itemId: string,
  contactId: string,
  input: RespondInput,
): Promise<Confirmation> {
  const parsed = RespondInputSchema.safeParse(input);
  if (!parsed.success) {
    // A failed parse always carries at least one issue, so there is nothing
    // to fall back to.
    throw new ConfirmationError(parsed.error.issues[0]!.message);
  }

  const repo = context.data.collection<ScheduleItem>(
    { companyId: context.companyId, jobId: context.jobId },
    'scheduleItems',
  );
  const item = await repo.get(itemId);
  if (!item) throw new NotFoundError('scheduleItems', itemId);

  if (
    !item.assignees.some((assignee) => assignee.kind === 'contact' && assignee.id === contactId)
  ) {
    throw new ConfirmationError('You are not assigned to that schedule item');
  }

  const now = context.clock.now().toISOString();
  const answer: Confirmation = {
    status: parsed.data.status as ConfirmationStatus,
    at: now,
    note: parsed.data.note ? parsed.data.note : undefined,
  };

  const writes: BatchWrite[] = [
    updateWrite<ScheduleItem>(
      { companyId: context.companyId, jobId: context.jobId },
      'scheduleItems',
      itemId,
      {
        confirmations: { ...item.confirmations, [contactId]: answer },
        updatedAt: now,
        updatedBy: context.uid,
      },
      expectUnchanged(item),
    ),
  ];

  // The notification is *not* written here, and must not be: the rules
  // allow nobody but the server to create one, because a sub who could
  // create a notification could forge a record of being told something.
  // Putting it in this batch made the whole answer fail — found by the
  // emulator tests, which are the only place real rules apply.
  //
  // It is raised instead by `onConfirmationAnswered` in `apps/functions`,
  // which sees this write and notifies the people running the job.
  await context.data.commit(writes);
  return answer;
}

/** Items on a job awaiting an answer from one sub, for their own list. */
export async function pendingForSub(
  context: SchedulePlanContext,
  contactId: string,
): Promise<ScheduleItem[]> {
  const items = await listScheduleItems(context);
  return items.filter((item) => item.confirmations[contactId]?.status === 'pending');
}
